// liquid.ts — liquid-glass blob overlay using the WICG html-in-canvas proposal.
// Usage from main.ts:
//
//   import { initLiquidGlass } from './liquid';
//   initLiquidGlass();
//
// If the browser doesn't support ctx.drawElement (Chrome Canary +
// chrome://flags/#canvas-draw-element), this is a no-op and your portfolio
// renders normally.

interface CanvasRenderingContext2DWithDrawElement extends CanvasRenderingContext2D {
  drawElement?: (el: Element, x: number, y: number) => void;
  drawElementImage?: (el: Element, x: number, y: number) => void;
}

type Point = { x: number; y: number };

export function initLiquidGlass(): void {
  // ---- Feature detect ----
  const probe = document.createElement('canvas')
    .getContext('2d') as CanvasRenderingContext2DWithDrawElement | null;
  const HAS_NATIVE = !!probe && (
    typeof probe.drawElement === 'function' ||
    typeof probe.drawElementImage === 'function'
  );
  if (!HAS_NATIVE) return;

  const main = document.querySelector('main');
  if (!main) return;

  // ---- Wrap <main> in a <canvas layoutsubtree> ----
  const pageCanvas = document.createElement('canvas');
  pageCanvas.id = 'pageCanvas';
  pageCanvas.setAttribute('layoutsubtree', '');
  pageCanvas.style.cssText =
    'display:block;width:100vw;height:100dvh;position:relative;';
  main.parentNode!.insertBefore(pageCanvas, main);
  pageCanvas.appendChild(main);

  // ---- Lens overlay ----
  const lens = document.createElement('canvas');
  lens.id = 'lens';
  lens.style.cssText =
    'position:fixed;top:0;left:0;width:100vw;height:100vh;' +
    'pointer-events:none;z-index:9999;';
  document.body.appendChild(lens);

  const gl = lens.getContext('webgl', { premultipliedAlpha: false });
  const pageCtx = pageCanvas.getContext(
    '2d'
  ) as CanvasRenderingContext2DWithDrawElement | null;
  if (!gl || !pageCtx) return;

  // ---- Shaders ----
  const VS = `attribute vec2 p;varying vec2 v;
void main(){v=p*0.5+0.5;gl_Position=vec4(p,0.,1.);}`;

  const FS = `precision highp float;
varying vec2 v;
uniform sampler2D tex;
uniform vec2 pageSize;
uniform vec2 lensSize;
uniform float radius;
uniform float bevel;
uniform vec2 trail[14];
uniform vec2 scroll;

float sdSeg(vec2 p, vec2 a, vec2 b, float r){
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa,ba)/max(dot(ba,ba),1e-4), 0.0, 1.0);
  return length(pa - ba*h) - r;
}
float smin(float a, float b, float k){
  float h = clamp(0.5 + 0.5*(b-a)/k, 0.0, 1.0);
  return mix(b, a, h) - k*h*(1.0-h);
}
float sdTrail(vec2 p){
  float d = 1e6;
  for(int i = 0; i < 13; i++){
    float di = sdSeg(p, trail[i], trail[i+1], radius);
    d = smin(d, di, 18.0);
  }
  return d;
}

void main(){
  vec2 lp = v * lensSize;
  lp.y = lensSize.y - lp.y;

  float d = sdTrail(lp);
  vec3 base = texture2D(tex, (lp + scroll) / pageSize).rgb;

  vec2 shadowOff = vec2(0.0, 0.0);
  float ds = sdTrail(lp - shadowOff);
  float shadow = (1.0 - smoothstep(0.0, 18.0, ds)) * 0.025;
  vec3 outCol = base * (1.0 - shadow);

  if (d > 0.5) {
    gl_FragColor = vec4(outCol, 1.0);
    return;
  }

  float ring = 1.0 - smoothstep(-bevel, 0.0, d);
  float h = pow(1.0 - ring, 2.0);
  float slope = h * (1.0 - h) * 4.0;

  float e = 1.0;
  float dx = sdTrail(lp+vec2(e,0.)) - sdTrail(lp-vec2(e,0.));
  float dy = sdTrail(lp+vec2(0.,e)) - sdTrail(lp-vec2(0.,e));
  vec2 n = normalize(vec2(dx,dy)+1e-5);

  vec2 disp = -n * slope * bevel * 0.9;
  vec2 tangent = vec2(-n.y, n.x);
  float blurAmt = slope * bevel * 0.6;

  vec3 col = vec3(0.0);
  for (int i = 0; i < 5; i++) {
    float t = (float(i) / 4.0) - 0.5;
    vec2 off = disp + tangent * t * blurAmt;
    col += texture2D(tex, (lp + scroll + off) / pageSize).rgb;
  }
  col /= 5.0;

  float edge = smoothstep(-bevel * 0.5, 0.0, d);
  if (edge > 0.01) {
    vec2 caOff = -n * edge * 6.0;
    float R = texture2D(tex, (lp + scroll + disp + caOff) / pageSize).r;
    float B = texture2D(tex, (lp + scroll + disp - caOff) / pageSize).b;
    col.r = mix(col.r, R, edge);
    col.b = mix(col.b, B, edge);
  }

  col = col * 1.03 + 0.015;

  float rim = smoothstep(-1.5, 0.0, d) * (1.0 - smoothstep(0.0, 1.0, d));
  col += rim * 0.35;

  float topLight = max(0.0, -n.y);
  col += topLight * smoothstep(-bevel, -2.0, d) * 0.25;

  float a = 1.0 - smoothstep(-1.0, 0.5, d);
  gl_FragColor = vec4(mix(outCol, col, a), 1.0);
}`;

  function compile(type: number, src: string): WebGLShader {
    const sh = gl!.createShader(type)!;
    gl!.shaderSource(sh, src);
    gl!.compileShader(sh);
    if (!gl!.getShaderParameter(sh, gl!.COMPILE_STATUS)) {
      console.error(gl!.getShaderInfoLog(sh));
    }
    return sh;
  }

  const prog = gl.createProgram()!;
  gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
  gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
  gl.linkProgram(prog);
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW
  );
  const aP = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(aP);
  gl.vertexAttribPointer(aP, 2, gl.FLOAT, false, 0, 0);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  const uTex = gl.getUniformLocation(prog, 'tex');
  const uPageSize = gl.getUniformLocation(prog, 'pageSize');
  const uLensSize = gl.getUniformLocation(prog, 'lensSize');
  const uRadius = gl.getUniformLocation(prog, 'radius');
  const uBevel = gl.getUniformLocation(prog, 'bevel');
  const uTrail = gl.getUniformLocation(prog, 'trail[0]');
  const uScroll = gl.getUniformLocation(prog, 'scroll');

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  // ---- Resize handling ----
  function syncSize(): void {
    const w = Math.max(1, window.innerWidth);
    const h = Math.max(1, window.innerHeight);

    // 1. Force <main> (and size-full descendants like #ascii-canvas) to the
    //    new viewport size BEFORE we touch the canvas bitmap.
    (main as HTMLElement).style.width = w + 'px';
    (main as HTMLElement).style.height = h + 'px';
    (main as HTMLElement).style.display = 'block';

    // 2. Flush layout so the subtree reflows at the new size.
    void (main as HTMLElement).offsetWidth;

    // 3. Resize the canvas bitmap + lens to match.
    if (pageCanvas.width !== w) pageCanvas.width = w;
    if (pageCanvas.height !== h) pageCanvas.height = h;
    if (lens.width !== w) lens.width = w;
    if (lens.height !== h) lens.height = h;

    // 4. Flush again so layoutsubtree re-evaluates against the new bitmap box.
    void pageCanvas.offsetWidth;
  }
  syncSize();
  window.addEventListener('resize', syncSize);
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(syncSize).observe(document.documentElement);
  }

  function rasterizePage(): void {
    const fn = pageCtx!.drawElement || pageCtx!.drawElementImage;
    if (!fn) return;
    pageCtx!.reset();
    try {
      fn.call(pageCtx, main!, 0, 0);
    } catch (e) {
      // drawElement can throw mid-resize; just skip the frame.
    }
  }

  // ---- Trail state ----
  const N = 14;
  const trail: Point[] = [];
  for (let i = 0; i < N; i++) trail.push({ x: -9999, y: -9999 });
  const target: Point = { x: -9999, y: -9999 };
  const headVel: Point = { x: 0, y: 0 };
  let lastT = performance.now();

  function render(): void {
    syncSize();
    rasterizePage();

    const now = performance.now();
    const dt = Math.min(0.05, (now - lastT) / 1000);
    lastT = now;

    if (target.x < 0) {
      gl!.clear(gl!.COLOR_BUFFER_BIT);
      requestAnimationFrame(render);
      return;
    }

    if (trail[0].x < 0) {
      for (let i = 0; i < N; i++) {
        trail[i].x = target.x;
        trail[i].y = target.y;
      }
    }

    // Head: spring toward cursor
    const k = 280;
    const c = 22;
    const fx = k * (target.x - trail[0].x) - c * headVel.x;
    const fy = k * (target.y - trail[0].y) - c * headVel.y;
    headVel.x += fx * dt;
    headVel.y += fy * dt;
    trail[0].x += headVel.x * dt;
    trail[0].y += headVel.y * dt;

    // Tail: each point eases toward the previous
    for (let i = 1; i < N; i++) {
      const ease = 0.55 * Math.pow(0.9, i);
      trail[i].x += (trail[i - 1].x - trail[i].x) * ease;
      trail[i].y += (trail[i - 1].y - trail[i].y) * ease;
    }

    // ---- Draw ----
    gl!.bindTexture(gl!.TEXTURE_2D, tex);
    gl!.texImage2D(
      gl!.TEXTURE_2D,
      0,
      gl!.RGBA,
      gl!.RGBA,
      gl!.UNSIGNED_BYTE,
      pageCanvas
    );

    gl!.viewport(0, 0, lens.width, lens.height);
    gl!.clearColor(0, 0, 0, 0);
    gl!.clear(gl!.COLOR_BUFFER_BIT);

    gl!.uniform1i(uTex, 0);
    gl!.uniform2f(uPageSize, pageCanvas.width, pageCanvas.height);
    gl!.uniform2f(uLensSize, lens.width, lens.height);
    gl!.uniform1f(uRadius, 75);
    gl!.uniform1f(uBevel, 50);
    gl!.uniform2f(uScroll, 0, 0);

    const flat = new Float32Array(N * 2);
    for (let i = 0; i < N; i++) {
      flat[i * 2] = trail[i].x;
      flat[i * 2 + 1] = trail[i].y;
    }
    gl!.uniform2fv(uTrail, flat);

    gl!.drawArrays(gl!.TRIANGLE_STRIP, 0, 4);
    requestAnimationFrame(render);
  }

  // ---- Input ----
  window.addEventListener('pointermove', (e: PointerEvent) => {
    target.x = e.clientX;
    target.y = e.clientY;
  });
  window.addEventListener('pointerleave', () => {
    target.x = -9999;
    target.y = -9999;
    for (let i = 0; i < N; i++) {
      trail[i].x = -9999;
      trail[i].y = -9999;
    }
    headVel.x = 0;
    headVel.y = 0;
  });

  requestAnimationFrame(render);
}
